import { FiAlertTriangle, FiLock } from "react-icons/fi";
import "./FichaCliente.css";

/*
 * La ficha del cliente vista por el entrenador: sólo lectura.
 *
 * Lo primero que se enseña son las banderas (lesiones, medicación, embarazo,
 * falta de autorización médica), porque es lo que cambia lo que se le puede
 * mandar. Lo demás va después, agrupado como lo rellenó él.
 */

const DICE = {
  sexo: { hombre: "Hombre", mujer: "Mujer", otro: "Otro", "prefiero-no-decir": "No lo dice" },
  objetivoPrincipal: {
    "perder-grasa": "Perder grasa",
    "ganar-musculo": "Ganar músculo",
    recomposicion: "Recomposición",
    mantener: "Mantenerse",
    "mejorar-salud": "Mejorar salud",
    rendimiento: "Rendimiento",
  },
  plazo: { "1-mes": "1 mes", "3-meses": "3 meses", "6-meses": "6 meses", "1-ano": "1 año", "sin-prisa": "Sin prisa" },
  nivel: { nunca: "Nunca ha entrenado", principiante: "< 1 año", intermedio: "1-3 años", avanzado: "> 3 años" },
  momentoDelDia: { manana: "Mañana", mediodia: "Mediodía", tarde: "Tarde", noche: "Noche", variable: "Variable" },
  trabajo: { sedentario: "Sedentario", "de-pie": "De pie", fisico: "Físico", "muy-fisico": "Muy físico" },
  calidadSueno: { mala: "Mala", regular: "Regular", buena: "Buena" },
  estres: { bajo: "Bajo", medio: "Medio", alto: "Alto" },
  pasosDiarios: { "menos-5000": "Poco", "5000-10000": "Normal", "mas-10000": "Mucho", "no-lo-se": "No lo sabe" },
  alcohol: { nunca: "Nunca", ocasional: "Ocasional", "fin-de-semana": "Fines de semana", diario: "A diario" },
  tipoDieta: {
    omnivora: "Come de todo",
    vegetariana: "Vegetariana",
    vegana: "Vegana",
    pescetariana: "Pescetariana",
    "sin-gluten": "Sin gluten",
    otra: "Otra",
  },
  cocinaEnCasa: { siempre: "Siempre", "casi-siempre": "Casi siempre", "a-veces": "A veces", "casi-nunca": "Casi nunca" },
  presupuesto: { ajustado: "Ajustado", normal: "Normal", "sin-limite": "Sin límite" },
  embarazoOPostparto: { no: "No", embarazo: "Embarazo", postparto: "Postparto" },
  autorizacionMedica: {
    si: "Sí, con visto bueno médico",
    no: "No lo ha consultado",
    "no-hace-falta": "Dice no necesitarla",
    "no-lo-se": "No lo sabe",
  },
};

const edad = (fechaNac) => {
  if (!fechaNac) return null;
  const d = new Date(fechaNac);
  const hoy = new Date();
  let a = hoy.getFullYear() - d.getFullYear();
  const m = hoy.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < d.getDate())) a -= 1;
  return a;
};

// Devuelve el valor legible, o null si no hay nada que enseñar
function legible(campo, valor) {
  if (valor === undefined || valor === null || valor === "") return null;
  if (Array.isArray(valor)) return valor.length ? valor.join(", ") : null;
  return DICE[campo]?.[valor] ?? String(valor);
}

const Bloque = ({ titulo, filas }) => {
  const utiles = filas.filter(([, v]) => v !== null && v !== undefined && v !== "");
  if (!utiles.length) return null;
  return (
    <div className="fc-bloque">
      <h4>{titulo}</h4>
      <ul className="fc-pares">
        {utiles.map(([k, v]) => (
          <li key={k}>
            <b>{k}</b>
            <span>{v}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

function FichaResumen({ ficha }) {
  if (!ficha?.datos) {
    return (
      <p className="fc-resumen-vacio">
        Este cliente todavía no ha rellenado su ficha. Sin ella no hay con qué
        montarle la rutina ni el plan: la plataforma se la recordará por el chat.
      </p>
    );
  }

  const d = ficha.datos;
  const v = (campo) => legible(campo, d[campo]);
  const salud = d.consentimientoSalud?.dado;

  return (
    <div className="fc-resumen">
      {/* Lo que cambia lo que se le puede mandar, primero */}
      {ficha.banderas?.length > 0 && (
        <div className="fc-banderas">
          {ficha.banderas.map((b) => (
            <div className="fc-bandera" key={b.tipo + b.texto}>
              <FiAlertTriangle aria-hidden="true" />
              <span>{b.texto}</span>
            </div>
          ))}
        </div>
      )}

      <Bloque
        titulo="Sobre él"
        filas={[
          ["Edad", edad(d.fechaNacimiento) ? `${edad(d.fechaNacimiento)} años` : null],
          ["Sexo", v("sexo")],
          ["Ciudad", v("ciudad")],
          ["Teléfono", v("telefono")],
        ]}
      />

      <Bloque
        titulo="Objetivo"
        filas={[
          ["Quiere", v("objetivoPrincipal")],
          ["Peso objetivo", d.pesoObjetivo ? `${d.pesoObjetivo} kg` : null],
          ["Plazo", v("plazo")],
          ["Por qué ahora", v("motivacion")],
          ["Qué intentó antes", v("intentosPrevios")],
        ]}
      />

      <Bloque
        titulo="Entrenamiento"
        filas={[
          ["Experiencia", v("nivel")],
          ["Días/semana", d.diasPorSemana ? `${d.diasPorSemana} días` : null],
          ["Minutos/sesión", d.minutosPorSesion ? `${d.minutosPorSesion} min` : null],
          ["Cuándo", v("momentoDelDia")],
          ["Dónde", v("dondeEntrena")],
          ["Material", v("material")],
          ["Le gusta", v("ejerciciosQueGustan")],
          ["Odia", v("ejerciciosQueOdia")],
          ["Deportes", v("deportes")],
        ]}
      />

      <Bloque
        titulo="Día a día"
        filas={[
          ["Trabajo", v("trabajo")],
          ["Duerme", d.horasSueno ? `${d.horasSueno} h` : null],
          ["Calidad del sueño", v("calidadSueno")],
          ["Estrés", v("estres")],
          ["Se mueve", v("pasosDiarios")],
          ["Alcohol", v("alcohol")],
        ]}
      />

      <Bloque
        titulo="Alimentación"
        filas={[
          ["Dieta", v("tipoDieta")],
          ["Alergias", v("alergias")],
          ["Intolerancias", v("intolerancias")],
          ["No come", v("alimentosQueNoCome")],
          ["Favoritos", v("alimentosFavoritos")],
          ["Comidas al día", d.comidasAlDia ? `${d.comidasAlDia}` : null],
          ["Cocina en casa", v("cocinaEnCasa")],
          ["Presupuesto", v("presupuesto")],
          ["Suplementos", v("suplementos")],
        ]}
      />

      {/* Salud: sólo si él dio permiso. Si no, se dice que no se puede ver. */}
      {salud ? (
        <Bloque
          titulo="Salud (autorizado por el cliente)"
          filas={[
            ["Lesión actual", v("lesionesActuales")],
            ["Lesiones pasadas", v("lesionesPasadas")],
            ["Le molesta", v("zonasMolestia")],
            ["Condiciones", v("condicionesMedicas")],
            ["Medicación", v("medicacion")],
            ["Cirugías recientes", v("cirugiasRecientes")],
            ["Embarazo/postparto", v("embarazoOPostparto")],
            ["Autorización médica", v("autorizacionMedica")],
            ["Notas", v("notasSalud")],
          ]}
        />
      ) : (
        <p className="fc-resumen-vacio">
          <FiLock aria-hidden="true" /> El cliente no ha autorizado a guardar
          datos de salud, así que no hay lesiones ni medicación registradas.
          Pregúntale antes de subirle la carga.
        </p>
      )}
    </div>
  );
}

export default FichaResumen;
